// mailBilderUebernahme.ts — T-236: Bilder aus dem Mailtext beim Weg in einen
// Vorgang. Gemeinsam für die geöffnete Mail (MailDetailWindow) und die Liste
// (mailListActions), damit beide Wege dasselbe fragen und dasselbe ablegen.
//
// Die Datei-Anhänge wandern beim Zuordnen zu einem bestehenden Vorgang von
// selbst mit (Mail-Ablage, T-2). Die Bilder aus dem Mailtext nicht — nach ihnen
// wird gefragt, und zwar VOR dem Archivieren: dabei vergibt Graph eine neue
// messageId, der Import über die alte liefe ins Leere.

import { graphMailService, type MailAttachment } from "../../services/graphMailService";
import { vorgangService } from "../../services/vorgangService";
import { waehleBilder } from "./BilderAuswahlDialog";
import { bildDateiname } from "./inlineBilder";
import type { MailVorgangZiel } from "./mailVorgangAssign";

/** Eintrag der Anhang-Auswahl im Erstell-Flow (`mailAttachments.items`). */
export type FlowBildAnhang = {
    messageId: string;
    attachmentId: string;
    name: string;
    originalName: string;
    size: number;
    inline: true;
};

/**
 * Bilder als eigene, nicht vorausgewählte Gruppe für „Vorgang erstellen" —
 * mit sprechendem Namen statt `image001.png`.
 */
export function bilderAlsFlowAnhaenge(bilder: MailAttachment[], datum: Date): FlowBildAnhang[] {
    return bilder.map((a) => ({
        messageId: a.messageId,
        attachmentId: a.id,
        name: bildDateiname(a.name, a.contentType, datum),
        originalName: a.name,
        size: a.size,
        inline: true,
    }));
}

/**
 * Popup vor dem Zuordnen: WELCHE Bilder sollen mit? T-287: mit Vorschau und
 * Einzelauswahl statt einer blinden Ja/Nein-Frage (Bilder heißen meist
 * `image001.png` — erst das Vorschaubild macht sie unterscheidbar). Ohne Bilder
 * wird nicht gefragt (`[]`). Die Datei-Anhänge stehen nur zur Info im Text —
 * sie kommen ohnehin. Abbrechen/ESC heißt „ohne Bilder".
 */
export async function frageBilderAuswahl<T>(
    bilder: T[],
    anzahlDateien: number,
    /**
     * `zuordnen`: die Mail wird gerade einem Vorgang zugeordnet (Dateien kommen
     * über die Mail-Ablage). `uebernehmen`: „Anhänge übernehmen" im Vorgang —
     * die Dateien werden jetzt übernommen.
     */
    art: "zuordnen" | "uebernehmen",
    ladeBild: (bild: T) => Promise<Blob>,
    beschrift: (bild: T) => string,
    deps: { waehle?: typeof waehleBilder; groesse?: (bild: T) => number | null | undefined } = {},
): Promise<T[]> {
    if (bilder.length === 0) return [];
    const eines = bilder.length === 1;
    const dateien =
        anzahlDateien === 0
            ? ""
            : anzahlDateien === 1
              ? `Die Datei im Anhang wird ${art === "zuordnen" ? "automatisch " : ""}in den Vorgang übernommen. `
              : `Die ${anzahlDateien} Dateien im Anhang werden ${art === "zuordnen" ? "automatisch " : ""}in den Vorgang übernommen. `;
    const ohne = art === "zuordnen" ? "zuordnen" : "übernehmen";
    const waehle = deps.waehle ?? waehleBilder;
    return waehle<T>({
        titel: eines ? "Bild aus dem Mailtext übernehmen?" : "Bilder aus dem Mailtext übernehmen?",
        text:
            dateien +
            (eines
                ? "Im Mailtext steht außerdem dieses Bild. Soll es als Datei mit in den Vorgang?"
                : `Im Mailtext stehen außerdem ${bilder.length} Bilder. Welche sollen als Dateien mit in den Vorgang?`),
        bilder,
        ladeBild,
        beschrift,
        groesse: deps.groesse,
        nein: anzahlDateien === 0 && art === "uebernehmen" ? "Abbrechen" : eines ? `Ohne Bild ${ohne}` : `Ohne Bilder ${ohne}`,
    });
}

/** Bilder für `POST mail/attachments/import` — mit sprechendem Namen statt `image001`. */
export function bilderAlsImportItems(bilder: MailAttachment[], datum: Date) {
    return bilder.map((a) => {
        const name = bildDateiname(a.name, a.contentType, datum);
        return {
            messageId: a.messageId,
            attachmentId: a.id,
            // Nur einen besseren Namen mitsenden (ohne Endung).
            name: name !== a.name ? name.replace(/\.[a-z0-9]+$/i, "") : undefined,
        };
    });
}

/**
 * Die Bilder in jedes gebundene Ziel importieren. Ablageziel ist der Vorgang
 * und SEIN Kunde — nicht der Kontakt der Mail. Wirft beim ersten Fehler; der
 * Aufrufer meldet ihn, die Zuordnung selbst bleibt davon unberührt.
 */
export async function uebernimmBilderInZiele(args: {
    mailbox: string;
    bilder: MailAttachment[];
    datum: Date;
    ziele: MailVorgangZiel[];
}): Promise<void> {
    const items = bilderAlsImportItems(args.bilder, args.datum);
    for (const ziel of args.ziele) {
        // Ein To-do-Ziel kennt nur seine Id; der Import braucht den Vorgang dazu.
        const vorgangId = ziel.vorgangId ?? (await vorgangService.getTodo(ziel.todoId as number)).vorgangId;
        if (vorgangId == null) throw new Error("todo without vorgang");
        const kunde = (await vorgangService.getVorgang(vorgangId)).kunde;
        const res = await graphMailService.importAttachments({
            mailbox: args.mailbox,
            contactId: kunde?.id ?? undefined,
            vorgangId,
            todoId: ziel.todoId,
            attachments: items,
        });
        if (res.results.some((r) => r.status === "error")) throw new Error("attachment import failed");
    }
}
